"use client";

import React from "react";
import { useInView } from "react-intersection-observer";
import { useLocation, useNavigationType } from "react-router-dom";

import config from "@/config";
import useCache from "@/hooks/useCache";
import useStateHandler from "@/hooks/useStateHandler";
import useTranslations from "@/i18n/useTranslations";
import NavbarHomeNavigation from "@/layouts/navigation/components/NavbarHomeNavigation";
import fetchWrapper from "@/services/fetchWrapper.service";
import { DropdownSort } from '@/shared-components/dropdown';
import HomeNavigation from "@/shared-components/home/components/HomeNavigation";
import Loader from "@/shared-components/loader/Loader";
import InfiniteScrollLoader from "@/shared-components/loader/special-loaders/InfiniteScrollLoader";
import StaticSeoHead from "@/shared-components/seo/StaticSeoHead";
import AppNavigationPage from "@/shared-components/wrappers/components/AppNavigationPage";
import { getSsrDiscoverBootstrap } from "@/utils/ssrDiscoverBootstrap";

import CommunityFeedItem from './components/CommunityFeedItem';
import { clearCommunityReturnPosition, getCommunityReturnPosition, useCommunityScrollRestoration } from './hooks/useCommunityScrollRestoration';
import { normalizeCommunityFeedItem } from './utils/communityFeedItems';
import { COMMUNITY_FILTERS, getCommunityFeedFilterConfig } from './utils/communityFilters';

import "./index.css";

const COMMUNITY_PREFETCH_MARGIN = 200;

function readInitialCommunitySeoPayload() {
  const payload = getSsrDiscoverBootstrap('community');
  return payload?.seoData && typeof payload.seoData === 'object' ? payload.seoData : null;
}

function CommunityFeedContent({ cacheKey, filterConfig, filterOptions, selectedFilterOption, onFilterChange, currentUserId, retryLabel, locationKey, navigationType, refreshOnMount }) {
  const { loading, error, loadMoreError, fetchData, loadMoreData, refetch } = useCache(
    cacheKey,
    `${config.apis.api.url}/block9/community`,
    {
      // Keep the loaded cursor pages while this history entry is revisited.
      // Replacing them with page one on Back collapses the document before
      // React Router can restore its saved scroll position.
      type: "@cache/no-expiration",
      injectToken: filterConfig.injectToken,
      optionalToken: filterConfig.optionalToken,
      includeSearchQueries: filterConfig.queryParams,
      cursorPagination: true,
      invalidateWhen: filterConfig.value === COMMUNITY_FILTERS.FOLLOWING ? ['FOLLOW_ACTION'] : [],
      // Back restores the history snapshot; entering or changing filters fetches a fresh feed.
      refreshOnMount,
      deferInvalidationOnMount: navigationType === 'POP' && !refreshOnMount,
    }
  );
  const isLoading = loading && !fetchData?.data;
  const renderableItems = React.useMemo(
    () => (fetchData?.data ?? [])
      .map((item, index) => {
        const normalizedItem = normalizeCommunityFeedItem(item, currentUserId);
        if (!normalizedItem) return null;
        return { key: `${item?.type}-${item?.data?._id ?? index}`, item: normalizedItem };
      })
      .filter(Boolean),
    [fetchData?.data, currentUserId]
  );
  const { itemsRef, rememberClickedItem, getReturnItemHeight } = useCommunityScrollRestoration({
    locationKey,
    navigationType,
    filter: filterConfig.value,
    itemCount: renderableItems.length,
  });
  const { ref: paginationRef, inView: paginationVisible, entry: paginationEntry } = useInView({
    rootMargin: `${COMMUNITY_PREFETCH_MARGIN}px 0px`,
  });
  const nextCursor = fetchData?.nextCursor;
  React.useEffect(() => {
    // The observer state can briefly describe the sentinel's old position after items append.
    const target = paginationEntry?.target;
    const bounds = target?.getBoundingClientRect();
    const paginationIsNearViewport = paginationVisible && bounds
      && bounds.top <= window.innerHeight + COMMUNITY_PREFETCH_MARGIN
      && bounds.bottom >= -COMMUNITY_PREFETCH_MARGIN;
    if (!loading && !error && !loadMoreError && nextCursor
      && (paginationIsNearViewport || renderableItems.length === 0)) {
      loadMoreData();
    }
  }, [loading, error, loadMoreError, nextCursor, paginationVisible, paginationEntry, renderableItems.length, loadMoreData]);

  if (isLoading) return <Loader thin />;

  return (
    <>
      <div className="home-community__controls">
        <DropdownSort
          defaultOption={selectedFilterOption}
          options={filterOptions}
          getOptionLabel={(option) => option.label}
          onChangeOption={(option) => onFilterChange(option.value)}
        />
      </div>

      <div className="home-community__items" ref={itemsRef} onClickCapture={rememberClickedItem}>
        {renderableItems.map((item) => (
          <div
            className="home-community__item"
            key={item.key}
            data-community-item-key={item.key}
            style={{ minHeight: getReturnItemHeight(item.key) }}
          >
            <CommunityFeedItem item={item.item} />
          </div>
        ))}
      </div>
      {nextCursor && !error && !loadMoreError && (
        <div ref={paginationRef} className="home-community__loader">
          <InfiniteScrollLoader />
        </div>
      )}
      {(error || loadMoreError) && (
        <button
          className="home-community__retry"
          type="button"
          onClick={error ? refetch : loadMoreData}
        >
          {retryLabel}
        </button>
      )}
    </>
  );
}

export default function HomeCommunity() {
  const { state } = useStateHandler();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [filterHasChanged, setFilterHasChanged] = React.useState(false);
  const currentUserId = state?.user_session?._id ?? null;
  const t = useTranslations('common.shared.community_feed');
  const [filterOption, setFilterOption] = React.useState(() =>
    navigationType === 'POP'
      ? getCommunityReturnPosition(location.key)?.filter ?? COMMUNITY_FILTERS.ALL
      : COMMUNITY_FILTERS.ALL
  );
  const [communitySeo, setCommunitySeo] = React.useState(() => readInitialCommunitySeoPayload());
  const filterOptions = React.useMemo(
    () => [
      { value: 'all', label: t('filters.all') },
      { value: 'following', label: t('filters.following') },
    ],
    [t]
  );
  const selectedFilterOption = filterOptions.find((option) => option.value === filterOption);
  const filterConfig = React.useMemo(
    () => getCommunityFeedFilterConfig(filterOption),
    [filterOption]
  );
  const feedCacheKey = `${filterConfig.cacheKey}:${currentUserId ?? 'guest'}`;

  function handleFilterChange(value) {
    clearCommunityReturnPosition(location.key);
    setFilterHasChanged(true);
    setFilterOption(value);
  }

  function handlePageClickCapture(event) {
    if (!event.target.closest('.home-community__item')) {
      clearCommunityReturnPosition(location.key);
    }
  }

  React.useEffect(() => {
    let ignore = false;

    async function loadCommunitySeo() {
      try {
        const { response, data } = await fetchWrapper.get(`${config.apis.api.url}/seo/discover/community`);
        if (ignore || !response?.ok || !data || typeof data !== 'object') {
          return;
        }

        setCommunitySeo(data);
      } catch (error) {
        if (!ignore) {
          console.error('Failed to load community SEO payload', error);
        }
      }
    }

    loadCommunitySeo();

    return () => {
      ignore = true;
    };
  }, []);

  return (
    <AppNavigationPage>
      <StaticSeoHead
        title={communitySeo?.title || "Art Community | Suarte"}
        description={communitySeo?.metaDescription || "Join Suarte's art community and explore the latest conversations around artworks, articles, posts, ratings, thoughts, and curated artlists from contemporary artists, galleries, and art lovers."}
        canonicalUrl={communitySeo?.canonicalUrl}
        canonicalPath={communitySeo?.canonicalPath || "/community"}
        robots={communitySeo?.robots}
        ogType={communitySeo?.ogType}
        image={communitySeo?.ogImage}
        pageType="CollectionPage"
        structuredData={communitySeo?.structuredData}
        breadcrumbItems={[
          { name: 'Home', url: 'https://suarte.art/' },
          { name: 'Community', url: 'https://suarte.art/community' },
        ]}
      />
      <div className="home-community__page" onClickCapture={handlePageClickCapture}>
        <HomeNavigation active="community" />
        <div className="home-community__navbar">
          <NavbarHomeNavigation active="community" />
        </div>
        <div className="home-community__content">
          <CommunityFeedContent
            key={feedCacheKey}
            cacheKey={feedCacheKey}
            filterConfig={filterConfig}
            filterOptions={filterOptions}
            selectedFilterOption={selectedFilterOption}
            onFilterChange={handleFilterChange}
            currentUserId={currentUserId}
            retryLabel={t('actions.retry')}
            locationKey={location.key}
            navigationType={navigationType}
            refreshOnMount={navigationType !== 'POP' || filterHasChanged}
          />
        </div>
      </div>
    </AppNavigationPage>
  );
}
